import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, CreditCard, User, Mail, Calendar, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AccountPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loadingPortal, setLoadingPortal] = useState(false);

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      const response = await axios.post(
        `${API}/billing/portal`,
        {},
        { headers: { 'user-id': user.id } }
      );
      
      if (response.data.status === 'success' && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error(response.data.message || 'Billing portal unavailable. Please contact support.');
      }
    } catch (error) {
      console.error('Billing portal error:', error);
      toast.error('Billing portal unavailable. Please contact support.');
    } finally {
      setLoadingPortal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft size={20} className="text-stone-600" />
            </button>
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-lg font-serif font-bold text-[#1C1917]">Account</span>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            className="text-stone-600 border-stone-300"
            data-testid="logout-btn"
          >
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-6">
          <h2 className="text-xl font-serif text-[#1C1917] mb-6 flex items-center gap-2">
            <User size={20} className="text-[#881337]" />
            Profile
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-[#881337] flex items-center justify-center text-white text-lg font-medium">
                {user?.partner1_name?.[0]}{user?.partner2_name?.[0]}
              </div>
              <div>
                <p className="font-medium text-[#1C1917]">
                  {user?.partner1_name} & {user?.partner2_name}
                </p>
                <p className="text-sm text-stone-500">{user?.email}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
                  <Mail size={14} />
                  Email
                </div>
                <p className="text-[#1C1917]">{user?.email}</p>
              </div>
              
              <div className="p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
                  <Calendar size={14} />
                  Member Since
                </div>
                <p className="text-[#1C1917]">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="text-xl font-serif text-[#1C1917] mb-6 flex items-center gap-2">
            <CreditCard size={20} className="text-[#881337]" />
            Subscription & Billing
          </h2>
          
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500 mb-1">Current Plan</p>
                  <p className="text-lg font-medium text-[#1C1917] capitalize">
                    {user?.subscription_tier === 'basic' ? 'Essentials' : user?.subscription_tier || 'Free'}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  user?.subscription_status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : user?.subscription_status === 'trialing'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-stone-100 text-stone-600'
                }`}>
                  {user?.subscription_status || 'Active'}
                </span>
              </div>
              
              {user?.trial_end_date && (
                <p className="text-sm text-stone-500 mt-2">
                  Trial ends: {formatDate(user.trial_end_date)}
                </p>
              )}
            </div>
            
            <Button
              onClick={handleManageBilling}
              disabled={loadingPortal}
              className="w-full bg-[#881337] hover:bg-[#9F1239] text-white py-6 rounded-xl"
              data-testid="manage-billing-btn"
            >
              {loadingPortal ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Billing
                </>
              )}
            </Button>
            
            <p className="text-xs text-stone-400 text-center">
              Update payment method, view invoices, or cancel subscription
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountPage;
